import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useApi, errMsg, ago } from "@/lib/api";
import { useMember } from "@/components/MemberProvider";
import { LessonItem } from "@/components/analyst/LessonItem";

const TABS = ["pending", "approved", "retired", "rejected"];

const AddLesson = ({ onAdd }) => {
  const api = useApi();
  const [text, setText] = useState("");
  const add = async () => {
    try { onAdd(await api("post", "/analyst/lessons", { text: text.trim() })); setText(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-2">
      <input data-testid="lessons-add-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={400} placeholder="Write your own lesson…" className="flex-1 min-w-0 bg-[#0B101D] border border-white/10 rounded-full px-3 py-1.5 text-sm" />
      <button data-testid="lessons-add-button" onClick={add} disabled={text.trim().length < 3} className="px-4 py-1.5 rounded-full bg-blue-600 text-xs font-semibold disabled:opacity-40">Add</button>
    </div>
  );
};

const FeedbackLog = ({ items }) => (
  <ul className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="feedback-log">
    {!items.length && <li className="text-xs text-slate-500">No feedback saved yet.</li>}
    {items.map((f) => (
      <li key={f.id} className="text-xs text-slate-300">
        <Link to={`/meeting/${f.meeting_id}?race=${f.race_number}`} className="text-blue-400 hover:underline">{f.track} R{f.race_number}</Link>{" "}
        <b>{f.horse}</b> · <span className="text-blue-300">{f.category}</span>: {f.text} <span className="text-slate-500">· {ago(f.at)}</span>
      </li>
    ))}
  </ul>
);

export const LessonsPanel = () => {
  const api = useApi();
  const { me } = useMember();
  const [tab, setTab] = useState("pending");
  const [lessons, setLessons] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const load = useCallback(async () => {
    try {
      const [l, f] = await Promise.all([api("get", "/analyst/lessons"), api("get", "/analyst/feedback")]);
      setLessons(l);
      setFeedback(f);
    } catch (e) { toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => { if (me?.analyst_owner) load(); }, [me, load]);
  if (!me?.analyst_owner) return null;
  const shown = lessons.filter((l) => l.status === tab);
  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5 space-y-4" data-testid="lessons-panel">
      <div>
        <h2 className="text-lg font-bold">Analyst lessons</h2>
        <p className="text-xs text-slate-400">Private to you. Approved lessons are added to future scans and analyses when they fit the race.</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button key={t} data-testid={`lessons-tab-${t}`} onClick={() => setTab(t)} className={`px-3 py-1 rounded-full text-xs capitalize ${tab === t ? "bg-blue-600 text-white" : "border border-white/10 text-slate-300"}`}>
            {t} ({lessons.filter((l) => l.status === t).length})
          </button>
        ))}
      </div>
      <ul className="space-y-2">
        {!shown.length && <li className="text-xs text-slate-500" data-testid="lessons-empty">No {tab} lessons.</li>}
        {shown.map((l) => <LessonItem key={l.id} l={l} onChange={(n) => setLessons((x) => x.map((y) => (y.id === n.id ? n : y)))} />)}
      </ul>
      <AddLesson onAdd={(l) => { setLessons((x) => [l, ...x]); setTab("pending"); }} />
      <div className="space-y-2 pt-2 border-t border-white/5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Your feedback log</p>
        <FeedbackLog items={feedback} />
      </div>
    </section>
  );
};
